import { ExternalLink, Dumbbell, FlaskConical, Shield, Heart, Shirt, Search } from 'lucide-react';
import { getAmazonLink, getAmazonDirectLink } from '../data/armoryProducts';
import { useState } from 'react';

const categoryIcons = {
  supplements: FlaskConical,
  equipment: Dumbbell,
  accessories: Shield,
  recovery: Heart,
  apparel: Shirt,
};

/**
 * Product recommendation card with Amazon ASIN images and dual links.
 */
const ProductRecommendation = ({ product, variant = 'compact' }) => {
  if (!product) return null;

  const searchLink = getAmazonLink(product.searchTerm || product.name);
  const directLink = getAmazonDirectLink(product.asin);
  const productImage = product.imageUrl || null;
  const FallbackIcon = categoryIcons[product.category] || Shield;

  const ImageWithFallback = ({ className, iconSize = "w-8 h-8" }) => {
    const [showFallback, setShowFallback] = useState(false);

    const handleLoad = (e) => {
      // Amazon returns a 1x1 placeholder for some ASINs — detect and fallback
      if (e.target.naturalWidth < 10 || e.target.naturalHeight < 10) {
        setShowFallback(true);
      }
    };

    return (
      <div className={`relative ${className}`}>
        {productImage && !showFallback ? (
          <img
            src={productImage}
            alt={product.name}
            className="w-full h-full object-contain"
            loading="lazy"
            onLoad={handleLoad}
            onError={() => setShowFallback(true)}
          />
        ) : null}
        {(!productImage || showFallback) && (
          <div className="flex flex-col items-center justify-center w-full h-full bg-stone-100">
            <FallbackIcon className={`${iconSize} text-stone-400`} />
            <span className="text-stone-400 text-[9px] mt-1 font-medium tracking-wide">{product.subcategory}</span>
          </div>
        )}
      </div>
    );
  };

  if (variant === 'compact') {
    return (
      <a
        href={directLink || searchLink}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 glass border border-stone-800 rounded-lg p-3 hover:border-blood-700 transition-all group cursor-pointer"
        data-testid={`product-rec-${product.id}`}
      >
        <div className="w-12 h-12 rounded bg-white shrink-0 flex items-center justify-center overflow-hidden">
          <ImageWithFallback className="w-12 h-12" iconSize="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-stone-200 text-sm font-semibold truncate group-hover:text-blood-400 transition-colors">
            {product.name}
          </p>
          <p className="text-stone-500 text-xs">{product.priceRange}</p>
        </div>
        <ExternalLink className="w-3.5 h-3.5 text-stone-600 group-hover:text-blood-500 transition-colors shrink-0" />
      </a>
    );
  }

  // Full card variant for Armory page
  return (
    <div
      className="glass border border-stone-800 rounded-lg overflow-hidden hover:border-blood-700 transition-all group"
      data-testid={`product-card-${product.id}`}
    >
      <div className="bg-white p-4 flex items-center justify-center h-40 overflow-hidden">
        <ImageWithFallback className="w-full h-full" iconSize="w-12 h-12" />
      </div>
      <div className="p-3">
        <p className="text-stone-200 font-semibold text-sm group-hover:text-blood-400 transition-colors line-clamp-1">
          {product.name}
        </p>
        <p className="text-stone-500 text-xs mt-1 line-clamp-2">{product.description}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-blood-400 font-bold text-sm">{product.priceRange}</span>
          <div className="flex items-center gap-1 text-yellow-500 text-xs">
            {'★'.repeat(Math.floor(product.rating))}
            <span className="text-stone-500">{product.rating}</span>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          {directLink && (
            <a
              href={directLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-blood-700 hover:bg-blood-600 text-white text-[11px] font-bold py-2 px-2 rounded text-center transition-colors"
              data-testid={`product-pick-${product.id}`}
            >
              Our Pick
            </a>
          )}
          <a
            href={searchLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 glass border border-stone-700 hover:border-blood-600 text-stone-300 hover:text-white text-[11px] font-bold py-2 px-2 rounded text-center transition-colors flex items-center justify-center gap-1"
            data-testid={`product-browse-${product.id}`}
          >
            <Search className="w-3 h-3" /> Browse Options
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProductRecommendation;
